import { useState, useCallback } from 'react';
import { ethers } from 'ethers';
import { toast } from 'react-hot-toast';
import { SWAPPER_ABI, ERC20_ABI } from '../contracts/abis';
import { CONTRACTS } from '../config/constants';
import { parseTokenAmount, calculateSlippage, getTransactionUrl } from '../utils/helpers';
import web3Provider from '../utils/web3Provider';

export const useSwapper = () => {
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapError, setSwapError] = useState(null);

  const executeSwap = useCallback(async ({
    fromToken,
    toToken,
    fromAmount,
    estimatedOutput,
    feeAmount,
    slippage
  }) => {
    if (!web3Provider.isConnected()) {
      throw new Error('Wallet not connected');
    }

    if (!CONTRACTS.SWAPPER) {
      throw new Error('Swapper contract not deployed');
    }

    setIsSwapping(true);
    setSwapError(null);

    let toastId;

    try {
      const signer = web3Provider.getSigner();
      const swapperContract = new ethers.Contract(
        CONTRACTS.SWAPPER,
        SWAPPER_ABI,
        signer
      );

      // Parse amounts
      const amountInWei = parseTokenAmount(fromAmount, fromToken.decimals);
      console.log('🔢 Swap calculations:');
      console.log('  fromAmount:', fromAmount);
      console.log('  estimatedOutput:', estimatedOutput);
      console.log('  feeAmount:', feeAmount);
      console.log('  slippage:', slippage);
      
      // estimatedOutput is already in wei from the contract - don't convert again!
      const expectedOutputWei = estimatedOutput;
      console.log('  expectedOutputWei:', expectedOutputWei);
      
      const amountOutMinWei = calculateSlippage(expectedOutputWei, slippage);
      console.log('  amountOutMinWei (with slippage):', amountOutMinWei);

      // Get token addresses
      const fromTokenAddress = fromToken.isNative ? ethers.ZeroAddress : fromToken.address;
      const toTokenAddress = toToken.isNative ? ethers.ZeroAddress : toToken.address;

      // Set deadline (20 minutes from now)
      const deadline = Math.floor(Date.now() / 1000) + (20 * 60);

      toastId = toast.loading('Preparing swap...');

      // Handle token approval if needed
      if (!fromToken.isNative) {
        toast.loading('Checking token allowance...', { id: toastId });
        
        const tokenContract = new ethers.Contract(fromToken.address, ERC20_ABI, signer);
        const allowance = await tokenContract.allowance(
          web3Provider.getAccount(),
          CONTRACTS.SWAPPER
        );

        if (allowance < ethers.toBigInt(amountInWei)) {
          toast.loading('Approving token spend...', { id: toastId });
          
          const approveTx = await tokenContract.approve(
            CONTRACTS.SWAPPER,
            ethers.MaxUint256
          );
          
          toast.loading('Waiting for approval confirmation...', { id: toastId });
          await approveTx.wait();
        }
      }

      // Execute swap
      toast.loading('Executing swap...', { id: toastId });
      
      console.log('🚀 Executing swap with parameters:');
      console.log('  fromTokenAddress:', fromTokenAddress);
      console.log('  toTokenAddress:', toTokenAddress);
      console.log('  amountInWei:', amountInWei);
      console.log('  amountOutMinWei:', amountOutMinWei);
      console.log('  deadline:', deadline);
      console.log('  msgValue:', fromToken.isNative ? amountInWei : '0');

      const swapTx = await swapperContract.swap(
        fromTokenAddress,
        toTokenAddress,
        amountInWei,
        amountOutMinWei,
        deadline,
        {
          value: fromToken.isNative ? amountInWei : 0,
          gasLimit: 300000 // Set gas limit
        }
      );

      toast.loading('Waiting for transaction confirmation...', { id: toastId });

      const receipt = await swapTx.wait();

      // Success
      toast.success(
        <div>
          <div>Swap successful!</div>
          <a 
            href={getTransactionUrl(receipt.transactionHash)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-300 underline text-sm"
          >
            View on Explorer
          </a>
        </div>,
        { id: toastId, duration: 8000 }
      );

      return {
        success: true,
        transactionHash: receipt.transactionHash,
        receipt
      };

    } catch (error) {
      console.error('Swap error:', error);
      
      let errorMessage = 'Swap failed';
      
      if (error.code === 4001) {
        errorMessage = 'Transaction rejected by user';
      } else if (error.code === -32603) {
        errorMessage = 'Insufficient funds for gas';
      } else if (error.message?.includes('insufficient funds')) {
        errorMessage = 'Insufficient balance';
      } else if (error.message?.includes('slippage')) {
        errorMessage = 'Slippage too high, try increasing slippage tolerance';
      } else if (error.message?.includes('deadline')) {
        errorMessage = 'Transaction deadline exceeded';
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, { id: toastId });
      setSwapError(errorMessage);

      return {
        success: false,
        error: errorMessage
      };

    } finally {
      setIsSwapping(false);
    }
  }, []);

  const resetSwapError = useCallback(() => {
    setSwapError(null);
  }, []);

  return {
    executeSwap,
    isSwapping,
    swapError,
    resetSwapError
  };
};